import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, ArrowUpRight, Clock } from 'lucide-react';

const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const ContactPage = () => {
  const { t } = useTranslation();

  const [heroRef,  heroVis]  = useReveal(0);
  const [cardsRef, cardsVis] = useReveal(0);
  const [mapRef,   mapVis]   = useReveal(0);
  const [infoRef,  infoVis]  = useReveal(150);

  const contactItems = [
    {
      Icon: Mail,
      label: t('contactPage.info.emailLabel'),
      value: 'Info@curevionsaludis.com',
      href: 'mailto:Info@curevionsaludis.com',
    },
    {
      Icon: Phone,
      label: t('contactPage.info.phoneLabel'),
      value: '+91 – 7669433416, +91 – 8796969361,  +91 – 8796969362',
      href: 'tel:+918796969361',
    },
    {
      Icon: MapPin,
      label: t('contactPage.info.locationLabel'),
      value: 'Rohini, Delhi , India',
      href: 'https://maps.google.com/?q=Rohini,Delhi,India',
    },
    // {
    //   Icon: Clock,
    //   label: t('contactPage.info.hoursLabel') || 'Business Hours',
    //   value: t('contactPage.info.hoursValue') || 'Mon – Sat, 9:00 AM – 6:00 PM IST',
    //   href: null,
    // },
  ];

  return (
    <div className="cp-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        @keyframes cpHeroIn { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
        @keyframes cpPulse  { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.6);opacity:0.3} }

        .cp-root { font-family:'Outfit',sans-serif; }

        /* ══ HERO ══ */
        .cp-hero {
          position:relative; min-height:300px;
          display:flex; align-items:center; justify-content:center;
          overflow:hidden; padding-top:80px;
          background:linear-gradient(135deg,#0d2233 0%,#1a3a4a 55%,#0f2d3d 100%);
        }
        .cp-hero-grid {
          position:absolute; inset:0; pointer-events:none;
          background-image:
            linear-gradient(rgba(58,143,163,0.07) 1px,transparent 1px),
            linear-gradient(90deg,rgba(58,143,163,0.07) 1px,transparent 1px);
          background-size:56px 56px;
        }
        .cp-hero-line {
          position:absolute; top:0; left:0; right:0; height:3px;
          background:linear-gradient(90deg,transparent,#3a8fa3,#5aab9a,#3a8fa3,transparent);
        }
        .cp-hero-orb1 {
          position:absolute; width:480px; height:480px; border-radius:50%;
          background:radial-gradient(circle,rgba(58,143,163,0.13) 0%,transparent 70%);
          top:-160px; right:-80px; pointer-events:none;
        }
        .cp-hero-orb2 {
          position:absolute; width:300px; height:300px; border-radius:50%;
          background:radial-gradient(circle,rgba(90,171,154,0.1) 0%,transparent 70%);
          bottom:-60px; left:8%; pointer-events:none;
        }
        .cp-hero-content {
          position:relative; z-index:2; text-align:center;
          padding:72px 24px 68px;
          opacity:0; transform:translateY(32px);
          transition:all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        .cp-hero-content.in { opacity:1; transform:translateY(0); }
        .cp-hero-eyebrow {
          display:inline-block; font-size:0.72rem; font-weight:600;
          letter-spacing:0.14em; text-transform:uppercase;
          color:#5aab9a; background:rgba(90,171,154,0.12);
          border:1px solid rgba(90,171,154,0.3);
          padding:5px 18px; border-radius:999px; margin-bottom:18px;
        }
        .cp-hero-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(2.6rem,6vw,4.5rem); font-weight:800;
          color:#fff; margin:0 0 14px; line-height:1.1;
          text-shadow:0 2px 24px rgba(0,0,0,0.3);
        }
        .cp-hero-sub {
          font-size:0.9rem; color:rgba(255,255,255,0.48);
          max-width:500px; margin:0 auto; line-height:1.8;
        }

        /* ══ BODY ══ */
        .cp-body {
          background:linear-gradient(180deg,#e8f5f7 0%,#d4eef2 60%,#e0f4f7 100%);
          padding:80px 0 90px; position:relative; overflow:hidden;
        }
        .cp-body::before {
          content:''; position:absolute; inset:0; pointer-events:none;
          background-image:radial-gradient(circle,rgba(58,143,163,0.055) 1px,transparent 1px);
          background-size:36px 36px;
        }
        .cp-blob { position:absolute; border-radius:50%; filter:blur(80px); pointer-events:none; }
        .cp-blob-tl { width:420px;height:420px;background:rgba(58,143,163,0.07);top:-100px;left:-100px; }
        .cp-blob-br { width:360px;height:360px;background:rgba(90,171,154,0.06);bottom:-80px;right:-80px; }
        .cp-inner { max-width:1200px;margin:0 auto;padding:0 32px;position:relative;z-index:1; }

        /* ══ CARDS ══ */
        .cp-cards {
          display:grid; grid-template-columns:repeat(3,1fr); gap:16px;
          margin-bottom:44px;
          opacity:0; transform:translateY(32px);
          transition:all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        @media(max-width:900px){ .cp-cards{grid-template-columns:repeat(2,1fr);} }
        @media(max-width:540px){ .cp-cards{grid-template-columns:1fr;} }
        .cp-cards.in { opacity:1; transform:translateY(0); }

        .cp-card {
          display:flex; flex-direction:column; gap:12px;
          padding:22px 20px; border-radius:20px;
          background:rgba(255,255,255,0.70);
          backdrop-filter:blur(20px) saturate(1.5);
          -webkit-backdrop-filter:blur(20px) saturate(1.5);
          border:1px solid rgba(255,255,255,0.9);
          box-shadow:0 4px 20px rgba(58,143,163,0.09),inset 0 1px 0 rgba(255,255,255,0.95);
          transition:all 0.35s cubic-bezier(0.4,0,0.2,1);
          text-decoration:none; color:inherit;
          position:relative; overflow:hidden;
        }
        .cp-card::after {
          content:''; position:absolute; bottom:0; left:0; right:0; height:3px;
          background:linear-gradient(90deg,#3a8fa3,#5aab9a);
          transform:scaleX(0); transform-origin:left;
          transition:transform 0.4s cubic-bezier(0.4,0,0.2,1);
        }
        .cp-card:hover {
          background:rgba(255,255,255,0.96);
          box-shadow:0 14px 44px rgba(58,143,163,0.18);
          border-color:rgba(58,143,163,0.2);
          transform:translateY(-6px);
        }
        .cp-card:hover::after { transform:scaleX(1); }

        .cp-card-top { display:flex; align-items:center; justify-content:space-between; }
        .cp-card-icon {
          width:44px; height:44px; border-radius:13px; flex-shrink:0;
          display:flex; align-items:center; justify-content:center;
          background:linear-gradient(135deg,rgba(58,143,163,0.12),rgba(90,171,154,0.1));
          border:1px solid rgba(58,143,163,0.18);
          transition:all 0.3s;
        }
        .cp-card:hover .cp-card-icon {
          background:linear-gradient(135deg,rgba(58,143,163,0.22),rgba(90,171,154,0.18));
          box-shadow:0 4px 14px rgba(58,143,163,0.22);
        }
        .cp-card-icon svg { width:18px; height:18px; color:#3a8fa3; }
        .cp-card-arrow { color:rgba(58,143,163,0.25); transition:all 0.3s; }
        .cp-card:hover .cp-card-arrow { color:#3a8fa3; transform:translate(3px,-3px); }
        .cp-card-label {
          font-size:0.68rem; font-weight:700; letter-spacing:0.1em;
          text-transform:uppercase; color:#3a8fa3;
        }
        .cp-card-value {
          font-size:0.88rem; color:#1a3a4a; font-weight:500; line-height:1.5;
        }

        /* ══ BOTTOM: MAP + DARK CARD ══ */
        .cp-bottom {
          display:grid; grid-template-columns:1.45fr 0.55fr; gap:22px; align-items:stretch;
        }
        @media(max-width:860px){ .cp-bottom{grid-template-columns:1fr;} }

        /* Map */
        .cp-map-wrap {
          border-radius:24px; overflow:hidden;
          box-shadow:0 12px 40px rgba(58,143,163,0.15);
          border:1px solid rgba(255,255,255,0.9);
          opacity:0; transform:translateX(-44px);
          transition:all 0.9s cubic-bezier(0.4,0,0.2,1);
          display:flex; flex-direction:column;
        }
        .cp-map-wrap.in { opacity:1; transform:translateX(0); }
        .cp-map-wrap iframe { width:100%; flex:1; min-height:340px; display:block; border:none; }
        .cp-map-bar {
          background:rgba(255,255,255,0.96);
          padding:14px 20px; display:flex; align-items:center; gap:10px;
          border-top:1px solid rgba(58,143,163,0.1);
        }
        .cp-map-dot {
          width:8px; height:8px; border-radius:50%; flex-shrink:0;
          background:linear-gradient(135deg,#3a8fa3,#5aab9a);
          animation:cpPulse 2.2s ease-in-out infinite;
        }
        .cp-map-addr { font-size:0.82rem; color:#3d5a6a; font-weight:500; }
        .cp-map-link {
          margin-left:auto; font-size:0.78rem; color:#3a8fa3; font-weight:600;
          text-decoration:none; display:flex; align-items:center; gap:4px;
          white-space:nowrap; transition:opacity 0.2s;
        }
        .cp-map-link:hover { opacity:0.7; }

        /* Dark info card */
        .cp-info-card {
          background:linear-gradient(145deg,#0d2233 0%,#1a3a4a 100%);
          border-radius:24px; padding:36px 28px;
          position:relative; overflow:hidden;
          box-shadow:0 16px 48px rgba(13,34,51,0.3);
          opacity:0; transform:translateX(44px);
          transition:all 0.9s cubic-bezier(0.4,0,0.2,1) 0.12s;
          display:flex; flex-direction:column;
        }
        .cp-info-card.in { opacity:1; transform:translateX(0); }
        .cp-info-grid-bg {
          position:absolute; inset:0; pointer-events:none;
          background-image:
            linear-gradient(rgba(58,143,163,0.07) 1px,transparent 1px),
            linear-gradient(90deg,rgba(58,143,163,0.07) 1px,transparent 1px);
          background-size:38px 38px;
        }
        .cp-info-top-line {
          position:absolute; top:0; left:0; right:0; height:3px;
          background:linear-gradient(90deg,transparent,#3a8fa3,#5aab9a,transparent);
        }
        .cp-info-orb {
          position:absolute; width:260px; height:260px; border-radius:50%;
          background:radial-gradient(circle,rgba(58,143,163,0.14) 0%,transparent 70%);
          bottom:-80px; right:-80px; pointer-events:none;
        }
        .cp-info-z { position:relative; z-index:1; flex:1; display:flex; flex-direction:column; }

        .cp-info-eyebrow {
          display:inline-block; font-size:0.68rem; font-weight:600;
          letter-spacing:0.14em; text-transform:uppercase;
          color:#5aab9a; background:rgba(90,171,154,0.12);
          border:1px solid rgba(90,171,154,0.28);
          padding:4px 14px; border-radius:999px; margin-bottom:14px;
          align-self:flex-start;
        }
        .cp-info-heading {
          font-family:'Playfair Display',serif;
          font-size:1.45rem; font-weight:800; color:#fff;
          margin:0 0 10px; line-height:1.25;
        }
        .cp-info-sub {
          font-size:0.84rem; color:rgba(255,255,255,0.42);
          line-height:1.8; margin-bottom:28px;
        }

        .cp-info-rows { margin-top:auto; display:flex; flex-direction:column; gap:0; }
        .cp-info-row {
          display:flex; align-items:center; gap:14px;
          padding:13px 0; border-top:1px solid rgba(255,255,255,0.07);
        }
        .cp-info-row:last-child { border-bottom:1px solid rgba(255,255,255,0.07); }
        .cp-info-row-icon {
          width:34px; height:34px; border-radius:10px; flex-shrink:0;
          background:rgba(58,143,163,0.15); border:1px solid rgba(58,143,163,0.25);
          display:flex; align-items:center; justify-content:center;
        }
        .cp-info-row-icon svg { width:14px; height:14px; color:#5aab9a; }
        .cp-info-row-lbl {
          font-size:0.64rem; color:rgba(255,255,255,0.32);
          font-weight:700; letter-spacing:0.09em; text-transform:uppercase; margin-bottom:2px;
        }
        .cp-info-row-val { font-size:0.83rem; color:rgba(255,255,255,0.72); font-weight:500; }
      `}</style>

      {/* ══ HERO ══ */}
      <section className="cp-hero">
        <div className="cp-hero-grid" />
        <div className="cp-hero-line" />
        <div className="cp-hero-orb1" /><div className="cp-hero-orb2" />
        <div ref={heroRef} className={`cp-hero-content ${heroVis ? 'in' : ''}`}>
          <div className="cp-hero-eyebrow">{t('contactPage.hero.eyebrow')}</div>
          <h1 className="cp-hero-title">{t('contactPage.hero.title')}</h1>
          <p className="cp-hero-sub">{t('contactPage.hero.subtitle')}</p>
        </div>
      </section>

      {/* ══ BODY ══ */}
      <section className="cp-body">
        <div className="cp-blob cp-blob-tl" /><div className="cp-blob cp-blob-br" />
        <div className="cp-inner">

          {/* ── 4 Contact Cards ── */}
          <div ref={cardsRef} className={`cp-cards ${cardsVis ? 'in' : ''}`}>
            {contactItems.map((item, i) => {
              const Tag = item.href ? 'a' : 'div';
              const extra = item.href
                ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
                : {};
              return (
                <Tag key={i} className="cp-card" {...extra}
                  style={{ transitionDelay: `${i * 70}ms` }}>
                  <div className="cp-card-top">
                    <div className="cp-card-icon"><item.Icon /></div>
                    {item.href && <span className="cp-card-arrow"><ArrowUpRight size={15} /></span>}
                  </div>
                  <div className="cp-card-label">{item.label}</div>
                  <div className="cp-card-value">{item.value}</div>
                </Tag>
              );
            })}
          </div>

          {/* ── Map + Dark Card ── */}
          <div className="cp-bottom">

            {/* Google Map — Rohini, Delhi */}
            {/* <div ref={mapRef} className={`cp-map-wrap ${mapVis ? 'in' : ''}`}>
              <iframe
                title="Curevion Saludis — Rohini, Delhi"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3499.5194793563283!2d77.11500931508304!3d28.7040820823729!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d01c3e83f3c45%3A0x68b5d6c43a16e0aa!2sRohini%2C%20Delhi%2C%20110086!5e0!3m2!1sen!2sin!4v1699000000000!5m2!1sen!2sin"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="cp-map-bar">
                <div className="cp-map-dot" />
                <span className="cp-map-addr">Rohini, Delhi – 110086, India</span>
                <a
                  href="https://maps.google.com/?q=Rohini,Delhi,India"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cp-map-link"
                >
                  {/* {t('contactPage.info.openMap') || 'Open in Maps'} <ArrowUpRight size={12} /> */}
                { /* </a>
              </div>
            </div> */}

            {/* Dark Info Card */}
            <div ref={infoRef} className={`cp-info-card ${infoVis ? 'in' : ''}`}>
              <div className="cp-info-grid-bg" />
              <div className="cp-info-top-line" />
              <div className="cp-info-orb" />
              <div className="cp-info-z">
                <div className="cp-info-eyebrow">{t('contactPage.info.title')}</div>
                <h3 className="cp-info-heading">{t('contactPage.info.description')}</h3>
                <p className="cp-info-sub">{t('contactPage.hero.subtitle')}</p>

                <div className="cp-info-rows">
                  {[
                    { Icon: Mail,   lbl: t('contactPage.info.emailLabel'),   val: 'Info@curevionsaludis.com' },
                    { Icon: Phone,  lbl: t('contactPage.info.phoneLabel'),    val: '+91 – 7669433416, +91 – 8796969361,  +91 – 8796969362' },
                    { Icon: MapPin, lbl: t('contactPage.info.locationLabel'), val: 'Rohini, Delhi' },
                    // { Icon: Clock,  lbl: t('contactPage.info.hoursLabel') || 'Hours', val: t('contactPage.info.hoursValue') || 'Mon–Sat · 9AM–6PM IST' },
                  ].map(({ Icon, lbl, val }, i) => (
                    <div key={i} className="cp-info-row">
                      <div className="cp-info-row-icon"><Icon /></div>
                      <div>
                        <div className="cp-info-row-lbl">{lbl}</div>
                        <div className="cp-info-row-val">{val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;